import { apiClient } from './http.api';

/**
 * Report API
 * 
 * Functions for generating and managing reports
 */
export const reportApi = {
  /**
   * Regenerate report (delete old report and prepare for new generation)
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Response with message and session_id
   */
  regenerateReport: async (sessionId) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/regenerate-report`
    );
    return response.data;
  },

  /**
   * Generate session report
   * @param {string} sessionId - Session ID
   * @param {Object} options - Optional parameters
   * @param {boolean} options.auto_send_faicon - Automatically send to Faicon after generation
   * @param {boolean} options.auto_send_digwatch - Automatically send to DigWatch after generation
   * @param {string} options.llm_model - LLM model to use (e.g., 'claude-sonnet-4-20250514', 'gpt-4o')
   * @param {Object} options.custom_prompts - Custom prompts to override defaults (key-value pairs of prompt names and texts)
   * @returns {Promise<Object>} Report response with report_id and status
   */
  generateSessionReport: async (sessionId, options = {}) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/generate`,
      options
    );
    return response.data;
  },

  /**
   * Get report configuration (editable prompts, available models, default prompts)
   * @returns {Promise<Object>} Report configuration object
   */
  getReportConfig: async () => {
    const response = await apiClient.get('/api/reports/config');
    return response.data;
  },

  /**
   * Get available LLM models
   * @returns {Array<Object>} List of available models with their details
   * @deprecated Use getReportConfig() instead
   */
  getAvailableModels: () => {
    // Hardcoded list - fallback if API is not available
    return [
      {
        value: 'claude-sonnet-4-20250514',
        label: 'Claude Sonnet 4',
        provider: 'Anthropic',
        description: 'Default model for high-quality report generation',
        isDefault: true
      },
      {
        value: 'gpt-4o',
        label: 'GPT-4o',
        provider: 'OpenAI',
        description: 'Alternative model for report generation',
        isDefault: false
      }
    ];
  },

  /**
   * Generate event report
   * @param {string} eventId - Event ID
   * @param {Object} options - Optional parameters
   * @returns {Promise<Object>} Report response with report_id and status
   */
  generateEventReport: async (eventId, options = {}) => {
    const response = await apiClient.post(
      `/api/reports/events/${eventId}/generate`,
      options
    );
    return response.data;
  },

  /**
   * Get session report status
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Report status information
   */
  getSessionReportStatus: async (sessionId) => {
    const response = await apiClient.get(`/api/reports/sessions/${sessionId}/report/status`);
    return response.data;
  },

  /**
   * Get event report status
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Report status information
   */
  getEventReportStatus: async (eventId) => {
    const response = await apiClient.get(`/api/reports/events/${eventId}/report/status`);
    return response.data;
  },

  /**
   * Get session report content
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Report content with data
   */
  getSessionReport: async (sessionId) => {
    const response = await apiClient.get(`/api/reports/sessions/${sessionId}/report`);
    return response.data;
  },

  /**
   * Get event report content
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Report content with data
   */
  getEventReport: async (eventId) => {
    const response = await apiClient.get(`/api/reports/events/${eventId}/report`);
    return response.data;
  },

  /**
   * Get report status (legacy - kept for backward compatibility)
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} Report status information
   */
  getReportStatus: async (reportId) => {
    const response = await apiClient.get(`/api/reports/${reportId}/status`);
    return response.data;
  },

  /**
   * Publish report to FAICON
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Response with published_url
   */
  publishToFaicon: async (sessionId) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/send-faicon`
    );
    return response.data;
  },

  /**
   * Publish report to WordPress
   * @param {string} sessionId - Session ID
   * @param {string} version - API version (v1 or v2, default: v2)
   * @returns {Promise<Object>} Response with published_url
   */
  publishToWordPress: async (sessionId, version = 'v2') => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/send-digwatch?version=${encodeURIComponent(version)}`
    );
    return response.data;
  },

  /**
   * Download session report as PDF
   * @param {string} sessionId - Session ID
   * @param {string} filename - Optional filename for the downloaded file
   * @returns {Promise<void>} Triggers file download
   */
  downloadSessionReportPdf: async (sessionId, filename = null) => {
    const response = await apiClient.get(
      `/api/reports/sessions/${sessionId}/report/pdf`,
      { responseType: 'blob' }
    );
    
    // Extract filename from Content-Disposition header if not provided
    if (!filename) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        filename = `session_report_${sessionId}.pdf`;
      }
    }
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download event report as PDF
   * @param {string} eventId - Event ID
   * @param {string} filename - Optional filename for the downloaded file
   * @returns {Promise<void>} Triggers file download
   */
  downloadEventReportPdf: async (eventId, filename = null) => {
    const response = await apiClient.get(
      `/api/reports/events/${eventId}/report/pdf`,
      { responseType: 'blob' }
    );
    
    // Extract filename from Content-Disposition header if not provided
    if (!filename) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        filename = `event_report_${eventId}.pdf`;
      }
    }
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

